import { Policy } from "../session-management-api";

/**
 * Evaluates a user preference against a policy and returns the appropriate value based on the policy.
 * @param userPreference The user's preference for the setting, if any.
 * @param policy The policy that governs the setting.
 * @param enforcedValue The value to return if the policy is enforced.
 * @param disallowedValue The value to return if the policy is disallowed.
 * @param defaultValue The default value to return if an unknown policy is encountered.
 * @returns The value that should be used based on the policy and user preference.
 */
export function evaluateUserPreferenceAgainstPolicy<T>({
  userPreference,
  policy,
  enforcedValue,
  disallowedValue,
  defaultValue,
}: {
  userPreference: T | undefined | null;
  policy: Policy;
  enforcedValue: T;
  disallowedValue: T;
  defaultValue?: T;
}): T {
  // If no default value is provided, use the disallowed value as the default
  if (defaultValue === undefined) {
    defaultValue = disallowedValue;
  }

  switch (policy) {
    case "Disallowed":
      // If the policy is disallowed, return the disallowed value
      return disallowedValue;
    case "Enforced":
      // If the policy is enforced, return the enforced value
      return enforcedValue;
    case "OptOut":
      // If the policy is opt-out, return the user preference if it exists, otherwise return the enforcced value
      return userPreference ?? enforcedValue;
    case "OptIn":
      // If the policy is opt-in, return the user preference if it exists, otherwise return the default value
      return userPreference ?? disallowedValue;
    default:
      return defaultValue;
  }
}

/**
 * Whether the policy allows the user to override the setting or not.
 * @param policy Which policy to check
 * @returns whether the user can override the setting or not.
 */
export function isChangeDisallowedByPolicy(policy?: Policy): boolean {
  if (!policy) {
    return false; // If no policy is provided, assume the user can change the setting
  }

  return policy === "Enforced" || policy === "Disallowed";
}
